import {
  BaseQueryFn,
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryError,
} from "@reduxjs/toolkit/query";
import { toast } from "react-toastify";
import { Mutex } from "async-mutex";
import { apiBaseUrl } from "../components/consts/api-url.const";
import { showSpotifyReconnect } from "../store/popupSlice";

const mutex = new Mutex();
const baseUrl = `${apiBaseUrl}`;

const baseQuery = fetchBaseQuery({
  baseUrl,
  prepareHeaders: (headers) => {
    const token = localStorage.getItem("token");
    const metaAdsPublicToken = localStorage.getItem("metaAdsPublicToken");

    if (token) headers.set("Authorization", `Bearer ${token}`);
    if (metaAdsPublicToken) headers.set("x-auth-token", metaAdsPublicToken);

    return headers;
  },
});

export const fetchbase: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  await mutex.waitForUnlock();
  let result = await baseQuery(args, api, extraOptions);

  const errorData = result?.error?.data as
    | { requireSpotifyReconnect?: boolean; requireMetaAdsLogin?: boolean; detail?: any }
    | undefined;

  if (errorData?.requireSpotifyReconnect) {
    api.dispatch(showSpotifyReconnect());
    return result;
  }

  if (errorData?.requireMetaAdsLogin) {
    // Clear Meta Ads public token and redirect to login
    localStorage.removeItem("metaAdsPublicToken");
    localStorage.removeItem("metaAdsUserEmail");
    localStorage.removeItem("metaAdsTokenExpiresAt");
    window.location.href = "/meta-ads-login";
    return result;
  }

  if (result?.error && result.error.status === 401) {
    const refreshToken = localStorage.getItem("refresh_token");

    if (refreshToken) {
      const release = await mutex.acquire();
      try {
        const refreshResponse = await fetch(
          `${baseUrl}/api/auth/refresh_token?refresh_token=${refreshToken}`
        );
        if (!refreshResponse.ok) throw new Error("Failed to refresh token");

        const refreshData = await refreshResponse.json();
        const newToken = refreshData?.access_token;

        if (newToken) {
          localStorage.setItem("token", newToken);
          result = await baseQuery(args, api, extraOptions);
        }
      } catch (err) {
        console.error("Token refresh failed:", err);
        localStorage.removeItem("token");
        localStorage.removeItem("refresh_token");
        api.dispatch(showSpotifyReconnect());
      } finally {
        release();
      }
    } else {
      api.dispatch(showSpotifyReconnect());
    }
  } else if (result?.error?.data) {
    // Don't show toast for banned artist errors - they're handled specifically in components
    const errorData = result.error.data as any;
    if (errorData?.isBanned !== true && errorData?.isBanned !== "true") {
      const msg = getFirstErrorFromObject(result.error.data);
      if (msg) toast.error(msg);
    }
  }

  return result;
};

function getFirstErrorFromObject(obj: any) {
  if (obj?.detail) {
    if (Array.isArray(obj.detail)) {
      const eItem = obj.detail[0];
      if (eItem) {
        return `${eItem.msg}`;
      }
    } else if (obj.detail) {
      return obj.detail.toString();
    }
  }
}
